import { useMemo, useState, useEffect } from "react";
import { Stack, IconButton } from "@fluentui/react";
import DOMPurify from "dompurify";

import styles from "./Answer.module.css";

import { BotAppResponse, getCitationFilePath } from "../../api";
import { parseBotAnswerToHtml } from "./AnswerParser";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import Markdown from "markdown-to-jsx";
import { TypewriterEffect } from "./TypewriterEffect";

interface Props {
    answer: BotAppResponse;
    isSelected?: boolean;
    isStreaming: boolean;
    skipTypewriter?: boolean;
    onTypewriterStatusChange?: (isTypewriting: boolean) => void;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
}

export const AnswerBot = ({
    answer,
    isSelected,
    isStreaming,
    skipTypewriter,
    onTypewriterStatusChange,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions
}: Props) => {
    //console.log("Answer", answer);
    const [isIconChecked, setIsIconChecked] = useState(false);
    const [isTypingComplete, setIsTypingComplete] = useState(isStreaming || !!answer.isHistory || !!skipTypewriter); // Track if typing is done
    const [hasStartedTyping, setHasStartedTyping] = useState(false); // Prevent restarts

    // const followupQuestions =  answer.choices[0].context.followup_questions;
    const messageContent = answer.message;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(messageContent, isStreaming, onCitationClicked), [messageContent, isStreaming, onCitationClicked]);

    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

    const toggleIcon = () => {
        setIsIconChecked(prevState => !prevState);
    };

    const handleButtonClick = () => {
        toggleIcon();
        navigator.clipboard.writeText(messageContent);
        setTimeout(() => {
            toggleIcon();
        }, 3500);
    };

    const handleTypingComplete = () => {
        if (!hasStartedTyping) {
            setHasStartedTyping(true);
        }
        setIsTypingComplete(true);
    };

    // Reset typing state when content changes
    useEffect(() => {
        if (isStreaming || answer.isHistory || skipTypewriter) {
            setIsTypingComplete(true);
            setHasStartedTyping(true);
        } else if (!hasStartedTyping) {
            setIsTypingComplete(false);
        }
    }, [messageContent, isStreaming, answer.isHistory, skipTypewriter, hasStartedTyping]);

    useEffect(() => {
        const isTypewriting = !isStreaming && !answer.isHistory && !skipTypewriter && !isTypingComplete;
        onTypewriterStatusChange?.(isTypewriting);
    }, [isStreaming, answer.isHistory, skipTypewriter, isTypingComplete, onTypewriterStatusChange]);

    const canShowExtras = isTypingComplete || !!answer.isHistory;
    const shouldShowFollowups = !answer.isHistory && (showFollowupQuestions ?? true);

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                    <div>
                        {/*    <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "Lightbulb" }}
                            title="Show thought process"
                            ariaLabel="Show thought process"
                            onClick={() => onThoughtProcessClicked()}
                            disabled={!answer.choices[0].context.thoughts?.length}
                        />
                        */}
                        {!isIconChecked ? (
                            <IconButton
                                style={{ color: "black" }}
                                iconProps={{ iconName: "ClipboardList" }}
                                title="Copy message to clipboard"
                                ariaLabel="Copy message to clipboard"
                                onClick={() => handleButtonClick()}
                            />
                        ) : (
                            <IconButton style={{ color: "black" }} iconProps={{ iconName: "CheckMark" }} />
                        )}
                    </div>
                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                {/* Use TypewriterEffect for completed responses, regular Markdown for streaming */}
                {isStreaming || answer.isHistory || skipTypewriter ? (
                    <Markdown>{sanitizedAnswerHtml}</Markdown>
                ) : (
                    <TypewriterEffect content={sanitizedAnswerHtml} isStreaming={false} speed={5} onComplete={handleTypingComplete} />
                )}
            </Stack.Item>

            {/* Only show citations after typing is complete */}
            {!!parsedAnswer.citations.length && canShowExtras && (
                <Stack.Item>
                    <Stack tokens={{ childrenGap: 5 }} styles={{ root: { flexDirection: "column" } }}>
                        <span className={styles.citationLearnMore}>Citations:</span>
                        {parsedAnswer.citations.map((x, i) => {
                            const path = getCitationFilePath(x);
                            return (
                                <a key={i} className={styles.citation} title={x} onClick={() => onCitationClicked(path)}>
                                    {`${++i}. ${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
            {/* Only show follow-up questions after typing is complete */}
            {!!answer.followup_questions?.length && onFollowupQuestionClicked && canShowExtras && shouldShowFollowups && (
                <Stack.Item>
                    <Stack
                        className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`}
                        tokens={{ childrenGap: 5 }}
                        styles={{ root: { flexDirection: "column" } }}
                    >
                        <span className={styles.followupQuestionLearnMore}>Follow-up questions:</span>
                        {answer.followup_questions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
        </Stack>
    );
};
